let tokenProvider: (() => Promise<string | null>) | undefined;
let nativeTransport:
  | ((path: string, options: RequestInit) => Promise<unknown>)
  | undefined;
export function setNativeTransport(transport?: typeof nativeTransport) {
  nativeTransport = transport;
}
let profileToken: string | undefined;
// Every setter call starts a new lifetime, including same-value session resets.
let credentialScope = {};
export function setProfileToken(token?: string) {
  profileToken = token;
  credentialScope = {};
}
export function setTokenProvider(provider?: () => Promise<string | null>) {
  tokenProvider = provider;
  credentialScope = {};
}
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  return request<T>(path, options, false) as Promise<T>;
}
export type ReverificationHint = { clerk_error: { type: 'forbidden'; reason: 'reverification-error'; metadata: { reverification: 'strict' } } };
/** Only a useReverification-wrapped caller should use this variant. Ordinary
 * API errors still throw; a verified server hint must reach Clerk's handler. */
export function apiWithReverification<T>(path: string, options: RequestInit = {}): Promise<T | ReverificationHint> {
  return request<T>(path, options, true);
}
async function request<T>(path: string, options: RequestInit, permitReverification: boolean): Promise<T | ReverificationHint> {
  if (nativeTransport) return nativeTransport(path, options) as Promise<T>;
  const scope = credentialScope, provider = tokenProvider, profile = profileToken;
  const signal = options.signal;
  const checkCurrent = () => {
    signal?.throwIfAborted();
    if (scope !== credentialScope)
      throw new DOMException('The request credential scope was retired.', 'AbortError');
  };
  checkCurrent();
  let token: string | null | undefined;
  let onAbort: (() => void) | undefined;
  try {
    const acquiring = provider?.();
    token = await (signal ? Promise.race([
      acquiring,
      new Promise<never>((_, reject) => {
        onAbort = () => reject(signal.reason);
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted) onAbort();
      }),
    ]) : acquiring);
  } catch (error) {
    checkCurrent();
    throw error;
  } finally {
    if (onAbort) signal?.removeEventListener('abort', onAbort);
  }
  checkCurrent();
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (profile) headers.set('X-Profile-Token', profile);
  if (options.body && !(options.body instanceof FormData))
    headers.set('Content-Type', 'application/json');
  const response = await fetch(`/api/v1${path}`, { ...options, headers });
  const data = await response.json().catch(() => ({
    error: {
      message: 'The service is unavailable. Please try again.',
      code: 'UNAVAILABLE',
    },
  }));
  if (!response.ok) {
    if (permitReverification && response.status === 403 &&
      data.clerk_error?.type === 'forbidden' && data.clerk_error?.reason === 'reverification-error' &&
      data.clerk_error?.metadata?.reverification === 'strict') return data as ReverificationHint;
    throw new ApiError(
      data.error?.message || 'Request failed',
      response.status,
      data.error?.code || 'REQUEST_FAILED',
    );
  }
  return data;
}
export const json = (value: unknown) => JSON.stringify(value);
